import type { AttributeFixField } from "./types";

export function formatAttributeValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "Leer";
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  if (Array.isArray(value)) return value.length ? value.map(formatAttributeValue).join(" · ") : "Leer";
  if (typeof value === "object") {
    const row = value as Record<string, unknown>;
    if ("value" in row) {
      return `${formatAttributeValue(row.value)}${row.unit ? ` ${row.unit}` : ""}${row.language_tag ? ` (${row.language_tag})` : ""}`;
    }
    return JSON.stringify(value);
  }
  return String(value);
}

export function fieldInputValue(field: AttributeFixField): string {
  if (field.inputValue === null || field.inputValue === undefined) return "";
  return String(field.inputValue);
}

function parseFieldValue(field: AttributeFixField, raw: string): string | number | boolean {
  if (!raw.trim()) throw new Error(`${field.label}: Bitte einen neuen Wert eintragen.`);
  let value: string | number | boolean = raw;
  if (field.valueType === "boolean") {
    if (raw !== "true" && raw !== "false") throw new Error(`${field.label}: Bitte Ja oder Nein auswählen.`);
    value = raw === "true";
  } else if (field.valueType === "number" || field.valueType === "integer") {
    // Accept a German decimal comma without accepting ambiguous thousands separators.
    if (!/^[+-]?(?:\d+(?:[.,]\d+)?|[.,]\d+)$/.test(raw.trim())) {
      throw new Error(`${field.label}: Bitte eine gültige Zahl eingeben.`);
    }
    value = Number(raw.trim().replace(",", "."));
    if (!Number.isFinite(value) || (field.valueType === "integer" && !Number.isSafeInteger(value))) {
      throw new Error(`${field.label}: ${field.valueType === "integer" ? "Bitte eine ganze Zahl eingeben." : "Zahl ist ungültig."}`);
    }
  } else if (field.valueType !== "string") {
    throw new Error(`${field.label}: Dieses Feld muss in Seller Central geprüft werden.`);
  }
  if (field.options.length && !field.options.some(option => option.value === value)) {
    throw new Error(`${field.label}: Bitte einen der von Amazon erlaubten Werte auswählen.`);
  }
  const rules = field.constraints;
  if (typeof value === "string") {
    if (rules.minLength != null && [...value].length < rules.minLength) throw new Error(`${field.label}: Mindestens ${rules.minLength} Zeichen erforderlich.`);
    if (rules.maxLength != null && [...value].length > rules.maxLength) throw new Error(`${field.label}: Höchstens ${rules.maxLength} Zeichen erlaubt.`);
  } else if (typeof value === "number") {
    if (rules.minimum != null && value < rules.minimum) throw new Error(`${field.label}: Der kleinste erlaubte Wert ist ${rules.minimum}.`);
    if (rules.maximum != null && value > rules.maximum) throw new Error(`${field.label}: Der größte erlaubte Wert ist ${rules.maximum}.`);
  }
  return value;
}

export function buildAttributeFix(fields: AttributeFixField[], values: Record<string, string>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const field of fields) {
    if (!field.editable || !Object.hasOwn(values, field.name)) continue;
    const raw = values[field.name];
    const value = parseFieldValue(field, raw);
    if (value === field.inputValue) continue;
    if (!field.valuePath.length) {
      result[field.name] = value;
      continue;
    }
    const template = structuredClone(field.template);
    let parent: unknown = template;
    for (const part of field.valuePath.slice(0, -1)) {
      if (!parent || typeof parent !== "object" || !Object.hasOwn(parent, part)) {
        throw new Error(`${field.label}: Felddaten bitte neu laden.`);
      }
      parent = (parent as Record<string | number, unknown>)[part];
    }
    const leaf = field.valuePath[field.valuePath.length - 1];
    if (!parent || typeof parent !== "object" || ["__proto__", "constructor", "prototype"].includes(String(leaf))) {
      throw new Error(`${field.label}: Feldstruktur kann nicht sicher geändert werden.`);
    }
    (parent as Record<string | number, unknown>)[leaf] = value;
    result[field.name] = template;
  }
  if (!Object.keys(result).length) throw new Error("Bitte mindestens einen Wert ändern, bevor du die Vorschau öffnest.");
  return result;
}
